import Form, { Field, useForm } from 'rc-field-form';
import React from 'react';

import { useDoorCodeMutation } from '~src/additional-info/door-code-mutation';
import { OpenModalButton } from '~src/commons/components/button/open-modal-button';
import { OpenModalButtonGroup } from '~src/commons/components/button/open-modal-button-group';
import { FormLayout } from '~src/commons/components/layout/form-layout';
import { useTranslations } from '~src/commons/utils/translations/translate';

import { AdditionalInfo, CategoryOption } from '../../api/api';
import { CategoryExtensionProps } from '../category/category-manager';
import { GeneralError } from '../commons/components/general-error';
import { FullscreenComponent, FullscreenMode } from '../fullscreen-internals/fullscreen-mode';
import { colors } from '../theme/colors';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { DialogLayout } from '../ui/dialog-layout';
import { TextInput } from '../ui/text-input';
import { useGlobalState, useSessionState } from './../global-state';

interface DoorCodeModalLayoutProps {
  fields: Record<string, React.ReactElement>;
  flags: any; // TODO:
}

const DoorCodeModalLayout: React.FC<DoorCodeModalLayoutProps> = ({ fields, flags }) => {
  return (
    <div css={styles.layout}>
      {flags.show_door_code && <div>{fields.door_code}</div>}
      {flags.show_courier_instructions && <div>{fields.courier_instructions}</div>}
    </div>
  );
};

interface DoorCodeModalProps {
  selectedOption: CategoryOption;
  onClose: () => void;
  onSubmit: (values: AdditionalInfo) => void;
}

export const DoorCodeModal: FullscreenComponent<DoorCodeModalProps> = ({
  selectedOption,
  onClose,
  onSubmit,
}) => {
  const state = useGlobalState();

  const [formInstance] = useForm();
  const { tICU } = useTranslations();

  const doorCodeField = (
    <Field name="door_code">
      <TextInput label={tICU('widget.DOORCODE_PLACEHOLDER')} maxLength={10} name="door_code" />
    </Field>
  );

  const courierInstructionsField = (
    <Field name="courier_instructions">
      <TextInput
        label={tICU('widget.COURIER_INSTRUCTIONS_PLACEHOLDER')}
        maxLength={200}
        name="courier_instructions"
        textarea
      />
    </Field>
  );

  return (
    <Dialog width={520} onClose={() => onClose()}>
      <DialogLayout title={tICU('widget.DELIVERY_INSTRUCTIONS_ADD')} onClose={onClose}>
        <Form
          css={{ height: '100%' }}
          initialValues={state.additional_info}
          form={formInstance}
          onFinish={onSubmit}
        >
          <FormLayout
            submitButton={
              <Button css={{ marginTop: '30px' }} type="submit">
                {tICU('widget.ADDRESS_MODAL.SAVE_BTN')}
              </Button>
            }
          >
            <DoorCodeModalLayout
              fields={{
                door_code: doorCodeField,
                courier_instructions: courierInstructionsField,
              }}
              flags={selectedOption.flags}
            />
          </FormLayout>
        </Form>
      </DialogLayout>
    </Dialog>
  );
};
DoorCodeModal.fullscreenName = 'DoorCodeModal';

export const DoorCodeExtension: React.FC<CategoryExtensionProps> = ({
  selected,
  selectedOption,
}) => {
  const { additional_info } = useGlobalState();
  const session = useSessionState();
  const { tICU } = useTranslations();

  const { mutate, error, reset } = useDoorCodeMutation();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  if (!selected) {
    return null;
  }
  const { show_door_code, show_courier_instructions } = selectedOption.flags || {};
  if (!show_door_code && !show_courier_instructions) {
    return null;
  }

  const onSubmit = (values: AdditionalInfo) => {
    mutate(values);
    setModalOpen(false);
  };

  const modalProxy = (
    <FullscreenMode>
      <DoorCodeModal
        selectedOption={selectedOption}
        onSubmit={onSubmit}
        onClose={() => {
          setModalOpen(false);
        }}
      />
    </FullscreenMode>
  );

  const openModal = () => {
    setModalOpen(true);
  };

  return (
    <>
      {additional_info?.door_code === undefined &&
      additional_info?.courier_instructions === undefined ? (
        <OpenModalButton
          onClick={openModal}
          title={{ text: tICU('widget.DELIVERY_INSTRUCTIONS_ADD'), color: colors.neutral35 }}
          ariaLabel={tICU('widget.DELIVERY_INSTRUCTIONS_ADD')}
          alterIcon={session.flags.enable_inline_category_extensions}
        />
      ) : (
        <OpenModalButtonGroup>
          {show_door_code && (
            <OpenModalButton
              title={{
                text: tICU('widget.DOORCODE_PLACEHOLDER'),
                color: colors.neutral35,
              }}
              description={{
                text: additional_info?.door_code || '',
                color: colors.black,
              }}
              onClick={openModal}
              alterIcon={session.flags.enable_inline_category_extensions}
            />
          )}
          {show_courier_instructions && (
            <OpenModalButton
              title={{
                text: tICU('widget.COURIER_INSTRUCTIONS_PLACEHOLDER'),
                color: colors.neutral35,
              }}
              description={{
                text: additional_info?.courier_instructions || '',
                color: colors.black,
              }}
              onClick={openModal}
              alterIcon={session.flags.enable_inline_category_extensions}
            />
          )}
        </OpenModalButtonGroup>
      )}
      {modalOpen && modalProxy}
      <GeneralError error={error} onClose={reset} />
    </>
  );
};

const styles = {
  layout: {
    display: 'grid',
    rowGap: '16px',
  },
};
